
import {
  Box,
  Button,
  Text,
  Image,
  useClipboard,
  Divider,
  Flex,
  Skeleton,
} from "@chakra-ui/react";
import { Footer } from "../../components/Footer";
import { theme } from "../../styles/theme";
import Navbar from "../../components/Navbar";
import { CardFormPayment } from "../../components/CardFormPayment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../services/api";

const toBRL = (n) => n.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' });
const fromCents = (c) => c / 100;

export function Payment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState({});
  const [orderInfos, setOrderInfos] = useState({});
  const [batchOrders, setBatchOrders] = useState([]);
  const [client, setClient] = useState({});
  const [invoiceStatus, setInvoiceStatus] = useState("");
  const [intervalId, setIntervalId] = useState(null);
  const [loadingPage, setLoadingPage] = useState(true);
  const { hasCopied, onCopy } = useClipboard(payment.pix_qrcode_text || "");
  const [couponDiscount, setCouponDiscount] = useState(null);
  const [firstDiscount, setFirstDiscount] = useState(null);
  const [breakdownByBatch, setBreakdownByBatch] = useState([]);
  const [startPolling, setStartPolling] = useState(false);
  const [cardSubmitted, setCardSubmitted] = useState(false);
  const hasRedirected = useRef(false);

  const handleCopy = (e) => {
    e.preventDefault();
    onCopy();
    setStartPolling(true);
    e.currentTarget.innerHTML = "Copiado!!";
    e.currentTarget.disabled = true;
  };

  async function fetchPayment() {
    const response = await api.get(`/api/v1/payments/${id}`);

    setCouponDiscount(response.data.coupon_discount ?? 0);
    setFirstDiscount(response.data.first_discount ?? 0);
    setPayment(response.data.payment);
    setOrderInfos(response.data.order_infos);
    setBatchOrders(response.data.batch_orders || []);
    setClient(response.data.user);

    const svcBreakdown = response.data.breakdown_by_batch || [];

    let metaBreakdown = [];
    if ((!svcBreakdown || svcBreakdown.length === 0) && response.data.order_infos?.batch_details) {
      metaBreakdown = response.data.order_infos.batch_details
        .map((bo) => {
          const meta = bo?.meta || bo?.item?.meta || {};
          const cartBatchId = bo?.item?.cart_batch_id || bo?.item?.id || bo?.id;
          if (
            typeof meta.original_subtotal_cents === 'number' &&
            typeof meta.final_amount_cents === 'number'
          ) {
            return {
              cart_batch_id: cartBatchId,
              breakdown: {
                originalSubtotalCents: meta.original_subtotal_cents || 0,
                deliveryFeeCents: meta.delivery_fee_cents || 0,
                couponDiscountCents: meta.coupon_discount_cents || 0,
                creditDiscountCents: meta.credit_discount_cents || 0,
                firstPurchaseDiscountCents: meta.first_purchase_discount_cents || 0,
                finalAmountCents: meta.final_amount_cents || Math.max(0, (meta.original_subtotal_cents || 0)
                  + (meta.delivery_fee_cents || 0)
                  - (meta.coupon_discount_cents || 0)
                  - (meta.credit_discount_cents || 0)
                  - (meta.first_purchase_discount_cents || 0)),
              }
            };
          }
          return null;
        })
        .filter(Boolean);
    }

    setBreakdownByBatch(svcBreakdown.length > 0 ? svcBreakdown : metaBreakdown);
    setLoadingPage(false);
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (response.data.payment?.payment_method === 'pix') {
      setStartPolling(true);
      localStorage.setItem('pendingPaymentId', String(response.data.payment.id));
      localStorage.setItem('pendingPaymentUserId', String(response.data.user?.id || ''));
    }
  }

  async function updateInvoiceStatus() {
    if (!payment?.id || hasRedirected.current) return;
    const response = await api.get(`/api/v1/payments/${payment.id}/invoice_status`);
    if (response.data.invoice.status === "paid") {
      hasRedirected.current = true;
      setInvoiceStatus(response.data.invoice_status);
      localStorage.removeItem('pendingPaymentId');
      localStorage.removeItem('pendingPaymentUserId');

      const items = orderInfos.batch_details?.map((item) => ({
        item_id: item.id,
        price: item.amount,
        quantity: item.quantity
      })) || [];
      if (window && window.gtag) {
        window.gtag("event", "purchase", {
          transaction_id: payment.id,
          value: (payment.price_in_cents / 100).toFixed(2),
          currency: "BRL",
          items
        });
      }

      navigate(`/perfil/${client.id}?payment_success=true`);
    } else {
      setInvoiceStatus(response.data.invoice_status);
    }
  }

  useEffect(() => { fetchPayment(); }, []);

  useEffect(() => {
    if (!startPolling || !payment?.id) return;

    updateInvoiceStatus();

    const interval = setInterval(() => {
      updateInvoiceStatus();
    }, 30000);

    return () => clearInterval(interval);
  }, [startPolling, payment]);

  useEffect(() => {
    if (!cardSubmitted || !payment?.id) return;

    let attempts = 0;

    const interval = setInterval(async () => {
      attempts++;
      await updateInvoiceStatus();
      if (attempts >= 10) clearInterval(interval);
    }, 5000);

    return () => clearInterval(interval);
  }, [cardSubmitted, payment]);


  const breakdownIndex = useMemo(() => {
    const idx = {};
    (breakdownByBatch || []).forEach(b => {
      idx[String(b.cart_batch_id)] = b.breakdown;
    });
    return idx;
  }, [breakdownByBatch]);

  const totals = useMemo(() => {
    let sumOriginal = 0, sumDelivery = 0, sumCupom = 0, sumCredit = 0, sumFirst = 0, sumFinal = 0;
    (orderInfos.batch_details || []).forEach((bo) => {
      const key = String(bo?.item?.cart_batch_id || bo?.item?.id || bo?.id);
      const bd = breakdownIndex[key];
      if (bd) {
        sumOriginal += bd.originalSubtotalCents || 0;
        sumDelivery += bd.deliveryFeeCents || 0;
        sumCupom    += bd.couponDiscountCents || 0;
        sumCredit   += bd.creditDiscountCents || 0;
        sumFirst    += bd.firstPurchaseDiscountCents || 0;
        sumFinal    += bd.finalAmountCents || 0;
      } else {
        const finalCents = Math.round((bo?.item?.amount || 0) * 100);
        sumFinal += finalCents;
      }
    });
    return { sumOriginal, sumDelivery, sumCupom, sumCredit, sumFirst, sumFinal };
  }, [orderInfos.batch_details, breakdownIndex]);

  const totalQuantity = useMemo(() =>
    (orderInfos.batch_details || []).reduce(
      (sum, bo) => sum + (Number(bo?.item?.quantity) || 0), 0
    ),
  [orderInfos.batch_details]);

  return (
    <Box bg="#f8f8f8" minH="100vh">
      <Navbar />

      {loadingPage ? (
        <Box pt={{ base: "120px", md: "160px" }} pb={{ base: "48px", md: "64px" }} px={{ base: "16px", md: "48px" }}>
          <Skeleton height="12px" width="100px" mb={3} borderRadius="8px" />
          <Skeleton height="36px" width="280px" mb={3} borderRadius="8px" />
          <Skeleton height="18px" width="420px" borderRadius="8px" />
          <Box display="grid" gridTemplateColumns={{ base: "1fr", lg: "1.2fr .8fr" }} gap="24px" maxW="1200px" mx="auto" mt={6}>
            <Skeleton height="420px" borderRadius="24px" />
            <Skeleton height="420px" borderRadius="24px" />
          </Box>
        </Box>
      ) : (
        <>
          {/* PAGE HEADER */}
          <Box
            bg="white"
            borderBottom="1px solid rgba(0,74,51,.06)"
            boxShadow="0 2px 12px rgba(0,0,0,.04)"
            pt={{ base: "120px", md: "160px" }}
            pb={{ base: "16px", md: "20px" }}
            px={{ base: "16px", md: "48px" }}
          >
            <Box maxW="1200px" mx="auto">
              <Text fontSize="11px" textTransform="uppercase" fontWeight="800" color="gray.400" letterSpacing="1.5px">
                Pedido #{payment.id} · {payment.payment_method === "pix" ? "PIX" : "Cartão de Crédito"}
              </Text>
              <Text mt={1} fontSize={{ base: "22px", md: "28px" }} fontWeight="900" color={theme.colors.primaryGreen}>
                Finalize seu pagamento
              </Text>
              <Text mt={1} color="gray.500" fontSize="13px">
                Pedido reservado. Complete o pagamento para confirmar.
              </Text>
            </Box>
          </Box>

          {/* MAIN GRID */}
          <Box maxW="1200px" mx="auto" px={{ base: "16px", md: "32px" }} py={{ base: "20px", md: "28px" }}>
            <Box
              display="grid"
              gridTemplateColumns={{ base: "1fr", lg: "1.2fr 0.8fr" }}
              gap="20px"
              alignItems="start"
            >

              {/* ── ORDER SUMMARY (left on desktop) ── */}
              <Box order={{ base: 2, lg: 1 }}>

                {/* Product list */}
                <Box bg="white" borderRadius="20px" border="1px solid rgba(0,74,51,.08)"
                  boxShadow="0 4px 20px rgba(0,74,51,.05)" overflow="hidden" mb="16px">
                  <Box bg={theme.colors.primaryGreen} px={5} py={4}
                    display="flex" alignItems="center" justifyContent="space-between">
                    <Text color="white" fontWeight="900" fontSize="15px">Itens do Pedido</Text>
                    <Box textAlign="right">
                      <Text color="rgba(255,255,255,.55)" fontSize="12px">
                        {(orderInfos.batch_details || []).length}{" "}
                        {(orderInfos.batch_details || []).length === 1 ? "produto" : "produtos"}
                      </Text>
                      {totalQuantity > 0 && (
                        <Text color="rgba(255,255,255,.40)" fontSize="11px">
                          {totalQuantity} {totalQuantity === 1 ? "unidade" : "unidades"} no total
                        </Text>
                      )}
                    </Box>
                  </Box>
                  <Box px={5} py={2}>
                    {(orderInfos.batch_details || []).map((batch_order, index0) => {
                      const key = String(
                        batch_order?.item?.cart_batch_id ||
                        batch_order?.item?.id ||
                        batch_order?.id
                      );
                      const bd = breakdownIndex[key];
                      const finalValue = bd
                        ? fromCents(bd.finalAmountCents || 0)
                        : (batch_order.item?.amount || 0);
                      const isLast = index0 === (orderInfos.batch_details || []).length - 1;

                      return (
                        <Box key={key} py={4}
                          borderBottom={isLast ? "none" : "1px solid rgba(0,74,51,.06)"}>
                          <Flex justify="space-between" alignItems="flex-start" gap={3}>
                            <Box flex={1}>
                              <Text fontWeight="700" fontSize="15px" color="#1a1a1a">
                                {batch_order.product?.name}
                              </Text>
                              {batch_order.item?.quantity > 0 && (
                                <Text fontSize="12px" fontWeight="700" color={theme.colors.primaryGreen} mt="3px">
                                  × {batch_order.item.quantity}{" "}
                                  {batch_order.batch?.unity_of_measurement || "un"}
                                  {batch_order.item.quantity > 1 ? "s" : ""}
                                </Text>
                              )}
                              <Flex gap={3} mt={1} flexWrap="wrap" alignItems="center">
                                {bd && bd.originalSubtotalCents > 0 &&
                                  bd.originalSubtotalCents !== bd.finalAmountCents && (
                                  <Text fontSize="12px" color="gray.400" textDecoration="line-through">
                                    {toBRL(fromCents(bd.originalSubtotalCents))}
                                  </Text>
                                )}
                                {bd && bd.deliveryFeeCents > 0 && (
                                  <Text fontSize="12px" color="gray.500">
                                    + {toBRL(fromCents(bd.deliveryFeeCents))} frete
                                  </Text>
                                )}
                                {bd && bd.deliveryFeeCents === 0 && (
                                  <Text fontSize="12px" color="green.600" fontWeight="700">
                                    Frete grátis
                                  </Text>
                                )}
                              </Flex>
                            </Box>
                            <Text fontWeight="900" color={theme.colors.primaryGreen} fontSize="16px" flexShrink={0}>
                              {toBRL(finalValue)}
                            </Text>
                          </Flex>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {/* Financial breakdown + total */}
                <Box bg="white" borderRadius="20px" border="1px solid rgba(0,74,51,.08)"
                  boxShadow="0 4px 20px rgba(0,74,51,.05)" p={5} mb="16px">
                  {(!!totals.sumDelivery || !!totals.sumCupom || !!totals.sumFirst || !!totals.sumCredit) && (
                    <Box mb={4}>
                      <Text fontSize="11px" fontWeight="800" color="rgba(0,74,51,.45)"
                        textTransform="uppercase" letterSpacing="0.08em" mb={3}>
                        Detalhes do valor
                      </Text>
                      {!!totals.sumDelivery && (
                        <Flex justify="space-between" mb={2}>
                          <Text fontSize="14px" color="gray.600">Frete</Text>
                          <Text fontSize="14px" fontWeight="600">{toBRL(fromCents(totals.sumDelivery))}</Text>
                        </Flex>
                      )}
                      {!!totals.sumCupom && (
                        <Flex justify="space-between" mb={2}>
                          <Text fontSize="14px" color="gray.600">Desconto cupom</Text>
                          <Text fontSize="14px" fontWeight="700" color="green.600">
                            - {toBRL(fromCents(totals.sumCupom))}
                          </Text>
                        </Flex>
                      )}
                      {!!totals.sumFirst && (
                        <Flex justify="space-between" mb={2}>
                          <Text fontSize="14px" color="gray.600">Primeira compra</Text>
                          <Text fontSize="14px" fontWeight="700" color="green.600">
                            - {toBRL(fromCents(totals.sumFirst))}
                          </Text>
                        </Flex>
                      )}
                      {!!totals.sumCredit && (
                        <Flex justify="space-between" mb={2}>
                          <Text fontSize="14px" color="gray.600">Créditos</Text>
                          <Text fontSize="14px" fontWeight="700" color="green.600">
                            - {toBRL(fromCents(totals.sumCredit))}
                          </Text>
                        </Flex>
                      )}
                      <Divider my={3} />
                    </Box>
                  )}
                  <Flex justify="space-between" alignItems="center">
                    <Box>
                      <Text fontSize="13px" color="gray.500" fontWeight="600">Total a pagar</Text>
                      <Text fontSize="11px" color="gray.400">
                        {payment.payment_method === "pix"
                          ? "via PIX · aprovação imediata"
                          : "via Cartão de Crédito"}
                      </Text>
                    </Box>
                    <Text fontSize={{ base: "28px", md: "36px" }} fontWeight="900"
                      color={theme.colors.primaryGreen} lineHeight={1}>
                      {toBRL((payment.price_in_cents ?? totals.sumFinal) / 100)}
                    </Text>
                  </Flex>
                </Box>

                {/* Delivery info */}
                {(() => {
                  const firstBo = (orderInfos.batch_details || [])[0];
                  const isCollect = firstBo?.item?.delivery_method === "collect";
                  const deliveryAddrStr = firstBo?.item?.delivery_address;
                  const collectAddr = firstBo?.collection_address;

                  return (
                    <Box bg="white" borderRadius="16px" border="1px solid rgba(0,74,51,.08)"
                      boxShadow="0 2px 12px rgba(0,74,51,.04)" p={4}>
                      <Flex gap={3} alignItems="flex-start">
                        <Text fontSize="22px" flexShrink={0} mt="1px">{isCollect ? "🏪" : "🚚"}</Text>
                        <Box flex={1}>
                          <Text fontWeight="800" fontSize="14px" color={theme.colors.primaryGreen}>
                            {isCollect ? "Local de Retirada" : "Endereço de Entrega"}
                          </Text>
                          {isCollect ? (
                            <>
                              {collectAddr?.address_street ? (
                                <>
                                  <Text fontSize="13px" color="gray.700" fontWeight="600" mt={1}>
                                    {collectAddr.address_street}, nº {collectAddr.address_number}
                                    {collectAddr.address_complement ? `, ${collectAddr.address_complement}` : ""}
                                    {collectAddr.address_neighborhood ? ` — ${collectAddr.address_neighborhood}` : ""}
                                  </Text>
                                  {collectAddr.address_postal_code && (
                                    <Text fontSize="12px" color="gray.400" mt="3px">
                                      CEP {collectAddr.address_postal_code}
                                    </Text>
                                  )}
                                </>
                              ) : (
                                <Text fontSize="13px" color="gray.600" mt={1}>
                                  O endereço do ponto de retirada será informado por e-mail após confirmação.
                                </Text>
                              )}
                              <Text fontSize="12px" color="gray.400" mt="6px">
                                ⏱ Disponível a partir de 8 dias úteis após a confirmação
                              </Text>
                            </>
                          ) : (
                            <>
                              {deliveryAddrStr ? (
                                <>
                                  <Text fontSize="13px" color="gray.700" fontWeight="600" mt={1}>
                                    {deliveryAddrStr}
                                  </Text>
                                  <Text fontSize="12px" color="gray.400" mt="6px">
                                    Prazo:{" "}
                                    <Box as="strong" color={theme.colors.primaryGreen}>2 a 8 dias úteis</Box>
                                    {" "}após confirmação
                                  </Text>
                                </>
                              ) : (
                                <>
                                  <Text fontSize="13px" color="gray.600" mt={1}>
                                    Após confirmação do pagamento:{" "}
                                    <Box as="strong" color={theme.colors.primaryGreen}>2 a 8 dias úteis</Box>
                                  </Text>
                                  <Text fontSize="12px" color="gray.400" mt={1}>
                                    Entregas para BH e Região Metropolitana · Você receberá atualizações por e-mail
                                  </Text>
                                </>
                              )}
                            </>
                          )}
                        </Box>
                      </Flex>
                    </Box>
                  );
                })()}
              </Box>

              {/* ── PAYMENT METHOD (right on desktop, top on mobile) ── */}
              <Box order={{ base: 1, lg: 2 }}>
                {payment.payment_method === "pix" && (
                  <Box bg="white" borderRadius="20px" border="1px solid rgba(0,74,51,.08)"
                    boxShadow="0 4px 20px rgba(0,74,51,.05)" overflow="hidden">

                    {/* Header */}
                    <Box bg={theme.colors.primaryGreen} px={5} py={4}>
                      <Flex alignItems="center" gap={3}>
                        <Text fontSize="22px">💠</Text>
                        <Box>
                          <Text color="white" fontWeight="900" fontSize="17px">
                            Pagamento via PIX
                          </Text>
                          <Text color="rgba(255,255,255,.55)" fontSize="12px">
                            Aprovação em minutos · sem taxas
                          </Text>
                        </Box>
                      </Flex>
                    </Box>

                    <Box p={5}>
                      {/* Status badge */}
                      <Box bg="orange.50" borderRadius="12px" p={3} mb={5}
                        border="1px solid" borderColor="orange.200"
                        display="flex" alignItems="center" gap={3}>
                        <Box w="10px" h="10px" borderRadius="50%" bg="orange.400" flexShrink={0} />
                        <Box>
                          <Text fontWeight="800" fontSize="13px" color="orange.700">
                            Aguardando pagamento
                          </Text>
                          <Text fontSize="11px" color="orange.500">
                            Escaneie o QR Code ou copie o código PIX abaixo
                          </Text>
                        </Box>
                      </Box>

                      {/* QR Code */}
                      <Box textAlign="center" mb={5}>
                        <Image
                          src={payment.pix_qrcode}
                          mx="auto"
                          width={{ base: "200px", md: "220px" }}
                          borderRadius="16px"
                          boxShadow="0 8px 24px rgba(0,0,0,.10)"
                        />
                      </Box>

                      {/* Copy button */}
                      <Button
                        h="52px"
                        w="100%"
                        bg={theme.colors.primaryGreen}
                        color={theme.colors.primaryOrange}
                        fontWeight="900"
                        fontSize="15px"
                        borderRadius="14px"
                        onClick={handleCopy}
                        border={`1.5px solid ${theme.colors.primaryGreen}`}
                        mb={5}
                        _hover={{
                          bg: "transparent",
                          color: theme.colors.primaryGreen,
                          borderColor: theme.colors.primaryGreen,
                        }}
                      >
                        Copiar código PIX
                      </Button>

                      {/* Steps */}
                      <Box bg="gray.50" borderRadius="14px" p={4} mb={4}>
                        <Text fontSize="11px" fontWeight="800" color="gray.400"
                          textTransform="uppercase" letterSpacing="0.08em" mb={3}>
                          Como pagar
                        </Text>
                        {[
                          "Abra o aplicativo do seu banco",
                          "Escolha a opção Pagar com PIX",
                          "Escaneie o QR Code ou cole o código copiado",
                          "Confirme e aguarde a aprovação automática"
                        ].map((step, i) => (
                          <Flex key={i} alignItems="flex-start" gap={3} mb={i < 3 ? 3 : 0}>
                            <Box
                              w="22px" h="22px" borderRadius="50%"
                              bg={theme.colors.primaryGreen}
                              color={theme.colors.primaryOrange}
                              display="flex" alignItems="center" justifyContent="center"
                              fontSize="11px" fontWeight="900" flexShrink={0}
                            >
                              {i + 1}
                            </Box>
                            <Text fontSize="13px" color="gray.600" lineHeight={1.4} pt="3px">
                              {step}
                            </Text>
                          </Flex>
                        ))}
                      </Box>

                      {/* Security badge */}
                      <Flex alignItems="center" justifyContent="center" gap={2}>
                        <Text fontSize="13px">🔒</Text>
                        <Text fontSize="11px" color="gray.400">
                          Transação segura e criptografada · Padrão Banco Central
                        </Text>
                      </Flex>
                    </Box>
                  </Box>
                )}

                {payment.payment_method === "credit_card" && (
                  <CardFormPayment
                    payment={payment}
                    batchOrders={batchOrders}
                    setCardSubmitted={setCardSubmitted}
                    updateInvoiceStatus={updateInvoiceStatus}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </>
      )}

      <Footer />
    </Box>
  );
}
